
import React, { useMemo, useState } from "react";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./WeeklyReportDownload.css";

// Update this import path to your existing API service.
import { ReportsServe } from "../../services/api/ReportsServ";

const WeeklyReportDownload = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [reportStatus, setReportStatus] = useState("idle");
  const [validationError, setValidationError] = useState("");
  const [reportData, setReportData] = useState([]);
  const [apiError, setApiError] = useState("");

  // Format date in local time to avoid UTC date shifts.
  const toApiDate = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const formatDate = (date) =>
    date
      ? new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(date)
      : "";

  const reportName = useMemo(() => {
    if (!fromDate || !toDate) return "Weekly Report";

    return `Weekly Report (${formatDate(fromDate)} - ${formatDate(toDate)})`;
  }, [fromDate, toDate]);

  const resetReport = () => {
    setReportStatus("idle");
    setReportData([]);
    setApiError("");
  };

  const handleFromDateChange = (event) => {
    const selectedDate = event.value;
    setFromDate(selectedDate);
    resetReport();
    setValidationError("");

    if (selectedDate && toDate && selectedDate > toDate) {
      setValidationError("From Date cannot be later than To Date.");
    }
  };

  const handleToDateChange = (event) => {
    const selectedDate = event.value;
    setToDate(selectedDate);
    resetReport();
    setValidationError("");

    if (selectedDate && fromDate && selectedDate < fromDate) {
      setValidationError("To Date must be on or after From Date.");
    }
  };

const downloadData = (data, fileName) => {
  const headerMapping = {
    lpLink: "LandingPageUrl",
    assetTitle: "PostTitle",
    content: "PostContent",
    assetImage: "FeaturedImageLink",
    resourceType: "Resource Type",
    jobFunction: "Parent Business Function",
    subFunction: "Child Business Function",
    industry: "Industry",
    subIndustry: "Sub Industry",
  };

  const columns = Object.keys(headerMapping);

  const updatedData = data.map((record) => {
    const row = {};

    columns.forEach((key) => {
      row[headerMapping[key]] = record[key] ?? "";
    });

    return row;
  });

  const worksheet = XLSX.utils.json_to_sheet(updatedData, {
    header: Object.values(headerMapping),
  });

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "LP Report");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const dataBlob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(dataBlob, fileName);
};

  const handleGenerateReport = async () => {
    if (!fromDate || !toDate) {
      setValidationError("Both From Date and To Date are required.");
      return;
    }

    if (fromDate > toDate) {
      setValidationError("From Date cannot be later than To Date.");
      return;
    }

    setValidationError("");
    setApiError("");
    setReportData([]);
    setReportStatus("loading");

    try {
      const payload = {
        fromDate: toApiDate(fromDate),
        toDate: toApiDate(toDate),
      };

      // Calls:
      // GET /reports/internal-lp-report?fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD
      const response = await ReportsServe.generateLPReport(payload);

      // Support common API response structures.
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : Array.isArray(response?.reports)
              ? response.reports
              : [];

      if (data.length === 0) {
        setReportStatus("empty");
        return;
      }

      setReportData(data);
      setReportStatus("success");
    } catch (error) {
      console.error("Failed to generate LP report:", error);
      setApiError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to generate the report. Please try again."
      );
      setReportStatus("error");
    }
  };

  const handleDownload = () => {
    if (!reportData.length) return;

    const fileName = `LP_Report_${toApiDate(
      fromDate
    )}_to_${toApiDate(toDate)}.xlsx`;

    downloadData(reportData, fileName);
  };

  const statusMessage = {
    idle: "Select the date range and generate the report.",
    loading: "Generating your report...",
    success: `${reportData.length} records generated successfully. Your report is ready to download.`,
    empty: "No records found for the selected date range.",
    error: "Unable to generate the report.",
  };

  return (
    <Card className="weekly-report-card">
      <div className="weekly-report-intro">
        <div className="weekly-report-icon">
          <i className="pi pi-file-excel" aria-hidden="true" />
        </div>

        <div>
          <h3>LP Classification Report</h3>
          <p>
            Select a date range, generate the report, and download
            the file.
          </p>
        </div>
      </div>

      <div className="weekly-report-controls">
        <div className="weekly-report-week-field">
          <label htmlFor="weeklyReportFromDate">From Date</label>
          <Calendar
            inputId="weeklyReportFromDate"
            value={fromDate}
            onChange={handleFromDateChange}
            showIcon
            readOnlyInput
            dateFormat="dd/mm/yy"
            maxDate={toDate || undefined}
            placeholder="Select start date"
            className="w-full"
          />
        </div>

        <div className="weekly-report-week-field">
          <label htmlFor="weeklyReportToDate">To Date</label>
          <Calendar
            inputId="weeklyReportToDate"
            value={toDate}
            onChange={handleToDateChange}
            showIcon
            readOnlyInput
            dateFormat="dd/mm/yy"
            minDate={fromDate || undefined}
            placeholder="Select end date"
            className="w-full"
          />
        </div>

        <Button
          label={reportStatus === "loading" ? "Generating..." : "Generate Report"}
          icon="pi pi-cog"
          onClick={handleGenerateReport}
          loading={reportStatus === "loading"}
          disabled={reportStatus === "loading"}
          className="generate-report-btn"
        />
      </div>

      {validationError && (
        <Message
          severity="error"
          text={validationError}
          className="weekly-report-message"
        />
      )}

      {apiError && (
        <Message
          severity="error"
          text={apiError}
          className="weekly-report-message"
        />
      )}

      <div className="weekly-report-download-row">
        <div className="weekly-report-file-icon">
          <i className="pi pi-file-excel" aria-hidden="true" />
        </div>

        <div className="weekly-report-file-details">
          <strong>{reportName}</strong>
          <span>{statusMessage[reportStatus]}</span>
        </div>

        <Button
          label="Download Report"
          icon="pi pi-download"
          outlined
          onClick={handleDownload}
          disabled={reportStatus !== "success" || !reportData.length}
          className="weekly-download-btn"
        />
      </div>

      {/* <Message
        severity="info"
        text="Select the date range, generate the report, then download the file."
        className="weekly-report-message"
      /> */}
    </Card>
  );
};

export default WeeklyReportDownload;
